// AI 创作视频 Seedance 2.0 生成模块
import { ModuleId } from "@xmiles/holopix-types";
import { useArtworkStore } from "@/store/artwork";
import { useUserStore } from "@/store/user";
import { AI_VIDEO_DEFAULT_MODE, AI_VIDEO_DEFAULT_MODEL_KEY, getAiVideoModuleId } from "@/views/workspace/components/ModulePanel/AiSeedancePanel/composables/aiVideoConfig";
import type { VideoQuality } from "@/views/workspace/components/ModulePanel/AiSeedancePanel/composables/aiVideoConfig";
import type {
  AiSeedanceParams,
  AiSeedanceGenerateRequest,
  ReferenceImage,
  ReferenceVideo
} from "@/views/workspace/components/ModulePanel/AiSeedancePanel/composables/types";
import { findClosestRatio, SEEDANCE_RATIO_OPTIONS } from "@/views/workspace/board/helper/generationConfig";

/** AI 创作视频默认模块 ID（全能参考） */
export const defaultModule = ModuleId.AiSeedanceReferenceVideo;

/** AI 创作视频默认参数（存储在 artworkStore.aivideo.params 中） */
export const aiSeedanceParams: AiSeedanceParams = {
  module: defaultModule,
  mode: AI_VIDEO_DEFAULT_MODE,
  modelKey: AI_VIDEO_DEFAULT_MODEL_KEY,
  vipChannel: true,
  apiClientId: "",
  source: 1,
  startTime: 0,
  prompt: "",
  quality: "480p" as VideoQuality,
  ratio: "16:9",
  duration: 5,
  referenceImages: [],
  referenceVideos: [],
  firstFrame: null,
  lastFrame: null
};

/**
 * 根据首帧图片推断最接近的 Seedance 比例值（图生/首尾帧场景）。
 * 与画布端 getImageDimensions + findClosestRatio 逻辑一致。
 *
 * 优先使用上传校验时已缓存的宽高，其次 OSS image/info 接口，最终回退到 Image 元素加载。
 */
async function inferCalcRatio(
  firstImageUrl: string,
  knownWidth?: number,
  knownHeight?: number,
): Promise<string | undefined> {
  if (!firstImageUrl) return undefined;

  // 1. 优先使用已知的图片尺寸（上传校验时已获取）
  if (knownWidth && knownHeight && knownWidth > 0 && knownHeight > 0) {
    return findClosestRatio(knownWidth, knownHeight, SEEDANCE_RATIO_OPTIONS);
  }

  // 2. 回退到 OSS image/info 接口
  try {
    const { getImageInfo } = await import("@xmiles/holopix-helper");
    const info = await getImageInfo(firstImageUrl);
    if (info.width > 0 && info.height > 0) {
      return findClosestRatio(info.width, info.height, SEEDANCE_RATIO_OPTIONS);
    }
  } catch {
    // fallback
  }

  // 3. 最终回退：通过 Image 元素加载获取尺寸（与画布端 getImageDimensions 一致）
  try {
    const { width, height } = await new Promise<{ width: number; height: number }>((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = firstImageUrl;
    });
    if (width > 0 && height > 0) {
      return findClosestRatio(width, height, SEEDANCE_RATIO_OPTIONS);
    }
  } catch {
    // fallback
  }

  return undefined;
}

/**
 * 将面板状态转换为后端生成请求参数
 *
 * 与画布端 buildSeedanceGenerateParams 的区别：
 * - 使用 getAiVideoModuleId（模式→模块一一对应，无 hasAssets 隐式文生通道）
 * - 不含画布专属字段（_videoTargetCid / movementAmplitude / bgm）
 */
export async function getAiSeedanceGenerateParam(): Promise<AiSeedanceGenerateRequest> {
  const artworkStore = useArtworkStore();
  const userStore = useUserStore();

  const params = artworkStore.aivideo.params as AiSeedanceParams;
  const mode = params.mode;
  const modelKey = params.modelKey ?? AI_VIDEO_DEFAULT_MODEL_KEY;
  const moduleId = getAiVideoModuleId(mode, modelKey);

  // 图生视频/首尾帧模式提交 "adaptive" 作为 videoRatio，真实比例通过 calcRatio 传递
  const isAutoRatioMode = mode === "image-to-video" || mode === "start-end";
  const videoRatio = isAutoRatioMode ? "adaptive" : params.ratio;

  let imageList: string[] = [];
  let videos: string[] | undefined;
  let firstImageUrl: string | undefined;
  let lastImageUrl: string | undefined;
  let lastFrameUrl: string | undefined;
  let multimodalImageUrls: string[] | undefined;
  let multimodalVideoUrls: string[] | undefined;
  let originImgUrl: string | undefined;
  let inputVideoDuration: number | undefined;
  let calcRatio: string | undefined;

  if (mode === "start-end") {
    // 首尾帧模式
    firstImageUrl = params.firstFrame?.image;
    lastImageUrl = params.lastFrame?.image;
    lastFrameUrl = lastImageUrl;
    originImgUrl = firstImageUrl;
    imageList = [firstImageUrl, lastImageUrl].filter(Boolean) as string[];
    // 图生/首尾帧：按首帧图片推断真实比例（优先使用已缓存的宽高）
    calcRatio = await inferCalcRatio(firstImageUrl!, params.firstFrame?.width, params.firstFrame?.height);
  } else if (mode === "reference") {
    // 全能参考模式
    imageList = params.referenceImages.map((img: ReferenceImage) => img.image);
    videos = params.referenceVideos.map((v: ReferenceVideo) => v.videoUrl);
    multimodalImageUrls = imageList;
    multimodalVideoUrls = videos;
    originImgUrl = imageList[0];
    // 全能参考：calcRatio 同步用户选择的比例
    calcRatio = params.ratio;
    // 参考视频总时长（与画布端一致：Math.round 取整，0 时传 undefined）
    if (videos?.length) {
      const total = params.referenceVideos.reduce((sum, v) => sum + (v.duration || 0), 0);
      inputVideoDuration = total > 0 ? Math.round(total) : undefined;
    }
  } else if (mode === "image-to-video") {
    // 图生视频模式
    imageList = params.referenceImages.map((img: ReferenceImage) => img.image);
    multimodalImageUrls = imageList;
    originImgUrl = imageList[0];
    // 图生：按首张图片推断真实比例（优先使用已缓存的宽高）
    calcRatio = await inferCalcRatio(imageList[0], params.referenceImages[0]?.width, params.referenceImages[0]?.height);
  }
  // text-to-video 模式不需要素材
  if (mode === "text-to-video") {
    calcRatio = params.ratio;
  }

  return {
    module: moduleId,
    userId: userStore.userInfo.id,
    uniqueId: userStore.userInfo.uniqueId,
    artworkId: artworkStore.artworkState.id,
    apiClientId: params.apiClientId,
    source: params.source,
    startTime: params.startTime || Date.now(),
    vipChannel: artworkStore.channel === "expedite",
    videoModel: modelKey,
    videoMode: mode,
    prompt: params.prompt.trim(),
    videoResolution: params.quality,
    videoRatio,
    calcRatio,
    videoDuration: params.duration,
    imageList,
    videos: videos?.length ? videos : undefined,
    firstImageUrl,
    lastImageUrl,
    lastFrameUrl,
    multimodalImageUrls,
    multimodalVideoUrls,
    inputVideoDuration,
    originImgUrl,
  };
}
